/* Hallmark · component: changelog-release-nav · genre: modern-minimal · theme: project system (core-frontend DESIGN.md)
 * A release-index rail for the long changelog: sticky vertical list of releases
 * with a CSS-slid active indicator (transform only, respects reduced-motion) and
 * IntersectionObserver scroll-spy. Rebuilt on our stack + tokens — the "line
 * sidebar" pattern (reactbits) without a motion library. Mobile falls back to a
 * native jump-to-release select. Closes the changelog audit's "no jump nav" gap.
 */
import { cn } from '@xhubio-saas/core-frontend/ui-components'
import { type ChangeEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type ChangelogRelease, formatReleaseDate } from './changelogMarkdown'

/** Smooth-scroll a release heading into view and reflect it in the URL hash. */
function jumpToRelease(id: string) {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  history.replaceState(null, '', `#${id}`)
}

/** Desktop rail: release list with scroll-spy + a sliding active line. Lives in
 * a static column (see ChangelogPage) so it is always visible; the document
 * scrolls in its own pane, which is passed as `scrollRoot` for the scroll-spy. */
export function ChangelogReleaseNav({
  releases,
  locale,
  scrollRoot,
  className,
}: {
  releases: ChangelogRelease[]
  locale: string
  /** The scroll container the release headings live in (the content pane). */
  scrollRoot?: Element | null
  className?: string
}) {
  const { t } = useTranslation()
  const listRef = useRef<HTMLUListElement>(null)
  const [activeId, setActiveId] = useState(releases[0]?.id ?? '')
  const [indicator, setIndicator] = useState({ top: 0, height: 0, ready: false })

  // Scroll-spy: the last release heading whose top has passed the pane's
  // activation line is the active one. A scroll listener on the pane is more
  // robust here than an IntersectionObserver (whose viewport-relative root
  // never fires when it's the pane, not the window, that scrolls).
  useEffect(() => {
    const root = scrollRoot
    if (!root) return
    const headings = releases
      .map((r) => root.querySelector<HTMLElement>(`#${CSS.escape(r.id)}`))
      .filter((el): el is HTMLElement => el !== null)
    if (headings.length === 0) return
    const onScroll = () => {
      const line = root.getBoundingClientRect().top + 96 // activation line below the pane top
      let current = headings[0]?.id
      for (const h of headings) {
        if (h.getBoundingClientRect().top - 1 <= line) current = h.id
        else break
      }
      if (current) setActiveId(current)
    }
    onScroll()
    root.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      root.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [releases, scrollRoot])

  // Slide the indicator to the active item (measured, so wrapped titles fit).
  useLayoutEffect(() => {
    const move = () => {
      const active = listRef.current?.querySelector<HTMLElement>(`[data-release-id="${activeId}"]`)
      if (active) setIndicator({ top: active.offsetTop, height: active.offsetHeight, ready: true })
    }
    move()
    window.addEventListener('resize', move)
    return () => window.removeEventListener('resize', move)
  }, [activeId])

  if (releases.length === 0) return null

  return (
    <nav className={cn('text-sm', className)} aria-label={t('changelog.releases', 'Releases')}>
      <p className="mb-3 pl-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {t('changelog.releases', 'Releases')}
      </p>
      <div className="relative">
        <span aria-hidden className="absolute top-0 bottom-0 left-0 w-px bg-border" />
        <span
          aria-hidden
          className="absolute left-0 w-0.5 rounded-full bg-primary transition-transform duration-200 ease-out motion-reduce:transition-none"
          style={{
            height: indicator.height,
            transform: `translateY(${indicator.top}px)`,
            opacity: indicator.ready ? 1 : 0,
          }}
        />
        <ul ref={listRef} className="space-y-0.5">
          {releases.map((r) => {
            const isActive = r.id === activeId
            return (
              <li key={r.id} data-release-id={r.id}>
                <a
                  href={`#${r.id}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveId(r.id)
                    jumpToRelease(r.id)
                  }}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'block rounded-sm py-1.5 pr-2 pl-4 transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                    isActive
                      ? 'font-medium text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <span className="block text-xs tabular-nums">
                    {formatReleaseDate(r.iso, locale)}
                  </span>
                  <span className="mt-0.5 line-clamp-2 text-xs">{r.title}</span>
                </a>
              </li>
            )
          })}
        </ul>
      </div>
    </nav>
  )
}

/** Mobile fallback: a native "jump to release" select above the document. */
export function ChangelogReleaseSelect({
  releases,
  locale,
  className,
}: {
  releases: ChangelogRelease[]
  locale: string
  className?: string
}) {
  const { t } = useTranslation()
  const onChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value) jumpToRelease(e.target.value)
  }, [])
  if (releases.length === 0) return null
  return (
    <label className={cn('block', className)}>
      <span className="sr-only">{t('changelog.jumpTo', 'Jump to release')}</span>
      <select
        onChange={onChange}
        defaultValue=""
        data-testid="changelog-jump-select"
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="" disabled>
          {t('changelog.jumpTo', 'Jump to release')}
        </option>
        {releases.map((r) => (
          <option key={r.id} value={r.id}>
            {formatReleaseDate(r.iso, locale)} — {r.title}
          </option>
        ))}
      </select>
    </label>
  )
}
